import { Injectable } from '@angular/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { AuthService } from '@klickdata/core/auth';
import { Customer } from '@klickdata/core/customer';
import { MessageService } from '@klickdata/core/message';
import { MobileService, SideNaveDataTypes } from '@klickdata/core/mobile';
import { ResourceOpportunity } from '@klickdata/core/opportunity';
import { User } from '@klickdata/core/user';
import { OccasionService } from '@klickdata/player/src/core/occasion/occasion.service';
import { TakeResActionValue } from '@klickdata/player/src/next-step.service';
import { ResourcePurchaseDialogComponent } from '@klickdata/shared-components/src/purchase/resource-purchase-dialog/resource-purchase-dialog.component';
import { OnDestroyHandler } from 'apps/klickdata/src/app/shared/onDestroy-handler/onDestroy-handler';
import * as moment from 'moment';
import { BehaviorSubject, filter, map, Observable, of, switchMap, takeUntil } from 'rxjs';
import { BypassResourceCheckKey, PlayableResourceSpec, Resource, ResourceCheckKeys } from './resource.model';
import { ResourceService } from './resource.service';
import { ResourceTypes } from './types.enum';
@Injectable({
    providedIn: 'root',
})
export class StartEventService extends OnDestroyHandler {
    public event: BehaviorSubject<Resource> = new BehaviorSubject<Resource>(null);
    public availableToJoin: PlayableResourceSpec = {
        isAllowed: true,
        clickable: true,
        label: $localize`Join`,
        icon: 'event_seat',
        checkType: ResourceCheckKeys.APPLICATION_DATE,
    };
    public mainEventType: { title: string; icon: string; color: string };
    public subEventType: { title: string; icon: string; color: string };
    public defaultbyPassEventCheck: BypassResourceCheckKey = {
        application_date: false,
        occasion_date: false,
        seats: false,
        enroll: false,
        price: false,
        items: false,
        other: false,
        opportunity: false,
        mandatory: false,
    };
    public moment = moment();
    private customer: Customer;
    private resourceName: string;
    constructor(
        protected resourceService: ResourceService,
        protected message: MessageService,
        protected auth: AuthService,
        protected bottomSheet: MatBottomSheet,
        protected mobile: MobileService,
        protected occasionService: OccasionService
    ) {
        super();
        auth.getCustomer()
            .pipe(takeUntil(this.destroy))
            .subscribe((customer) => (this.customer = customer));
    }
    public takeEvent(resource: Resource, user: User, eventJoin: PlayableResourceSpec): Observable<Resource> {
        if (eventJoin.isAllowed) {
            return this.requestStartEvent(resource, user);
        }
        if (eventJoin.checkType === ResourceCheckKeys.PRICE) {
            return this.requestPayment(resource, user);
        }
        return this.onNotAllowed(resource, user, eventJoin);
    }

    public requestPayment(resource: Resource, user: User): Observable<Resource> {
        return this.bottomSheet
            .open(ResourcePurchaseDialogComponent, {
                data: resource,
                panelClass: 'sheet-wrapper',
                disableClose: false,
            })
            .afterDismissed()
            .pipe(
                filter((status) => !!status),
                takeUntil(this.destroy),
                switchMap(() =>
                    resource.occasion_dates.length > 1
                        ? this.requestStartEvent(resource, user)
                        : this.resourceService
                              .joinEvent({
                                  resourceId: resource.id,
                                  userId: user.id,
                                  eventOccasionId: resource.occasion_dates[0].id,
                              })
                              .pipe(
                                  map((occ: ResourceOpportunity) => {
                                      resource.opportunity_id = occ.id;
                                      return resource;
                                  })
                              )
                )
            );
    }
    public requestStartEvent(resource: Resource, user: User): Observable<Resource> {
        const isEKGOpp = this.getEKGResource(resource);
        const mainEventType = ResourceTypes.getEventTypeByMainId(
            ResourceTypes.getEventTypes().find((type) => type.value == resource?.type_id)?.eventTypeId
        );
        if (!!resource.occasion_dates?.length) {
            resource.occasion_dates = resource.occasion_dates.map((item) => {
                return {
                    ...item,
                    isValid: new Date(item.end_date) > new Date(),
                };
            });
        }
        const subEventType = ResourceTypes.getEventTypeByTypeId(resource.type_id);
        const preventEnrollUntillOccasion = this.isPreventEnrollUntillOccasionDate(resource);

        if (preventEnrollUntillOccasion) {
            return this.mobile
                .onNotifyUser({
                    icon: subEventType.icon,
                    title: resource.title,
                    contentBody: $localize`You are allowed to join this ${mainEventType.title} ${subEventType.title} event \n But the enrollment is not available yet`,
                    positiveBtn: $localize`Ok`,
                })
                .pipe(switchMap(() => of(resource)));
        } else {
            this.mobile.updateSideNavSub({
                dataType: SideNaveDataTypes.TAKE_EVENT,
                data: {
                    icon: subEventType.icon,
                    title: resource.title,
                    resource: resource,
                    groupJoinEvent: user.canPlayAsGroupLeader(),
                    isEKGOpp: isEKGOpp,
                    user: user,
                },
            });
            return this.mobile.getSideNavResponseData().pipe(
                filter((data) => data.type === 'start' || data.type === 'signoff'),
                switchMap((data) => {
                    const response = this.makeAction(data, resource, isEKGOpp);
                    return response.pipe(map((occ) => ({ type: data.type, occ })));
                }),
                map((response: { type: string; occ: ResourceOpportunity }) => {
                    resource.opportunity_id = response.type === 'start' ? response.occ.id : null;
                    return resource;
                })
            );
        }
    }
    private getEKGResource(resource: Resource): boolean {
        return (
            !!resource.custom_fields?.length &&
            !!resource.custom_fields.find((cf) => cf.key === 'ekg') &&
            !!resource.custom_fields.find((cf) => cf.key === 'ekg').value
        );
    }
    private makeAction(data: any, resource: Resource, isEKGOpp: boolean) {
        if (data.type === 'start') {
            return this.onStart(data, resource);
        }
        if (data.type === 'signoff') {
            return this.onSignOff(data, resource, isEKGOpp);
        }
    }
    private onStart(data: any, resource: Resource) {
        return data.action === TakeResActionValue.GROUP_WORK_DONE
            ? this.occasionService.startOppWithGroup({
                  userIds: data.userIds,
                  resourceId: resource.id,
                  timeSpend: data.timeSpend,
                  eventOccasionId: data.eventOccasionId,
              })
            : this.occasionService.setOccasion(resource.id, { occasion_date_id: data.eventOccasionId });
    }
    private onSignOff(data: any, resource: Resource, isEKGOpp: boolean) {
        const doneEKGOppWithGroup = this.occasionService.doneEKGOppWithGroupAndResource(data.userIds, resource.id);

        const doneEKGAloneWithOpportunity = this.occasionService.doneAloneWithEKGOpp(
            { oppId: resource.opportunity_id },
            data.passedEGKCheck
        );
        const doneEKGAloneWithoutOpportunity = this.occasionService.doneAloneWithEKGOpp(
            { resourceId: resource.id },
            data.passedEGKCheck
        );

        const doneGroupWorkWithOpportunity = this.occasionService.doneGroupWork(
            resource.opportunity_id,
            data.userIds,
            true,
            data.timeSpend ? data.timeSpend : undefined
        );
        const doneGroupWorkWithoutOpportunity = this.occasionService.doneGroupWorkWithoutOpportunity(
            resource.id,
            data.userIds,
            true,
            data.timeSpend ? data.timeSpend : undefined
        );

        const doneWithOpportunity = this.occasionService.done(new ResourceOpportunity({ id: resource.opportunity_id }));
        const doneWithoutOpportunity = this.occasionService.doneWithoutOpportunity(resource.id);
        // Implementation //
        const isGroupWorkDone = data.action === TakeResActionValue.GROUP_WORK_DONE;
        const hasOpportunityId = resource.opportunity_id;

        if (isGroupWorkDone && isEKGOpp) {
            return doneEKGOppWithGroup;
        }

        if (isGroupWorkDone) {
            return hasOpportunityId ? doneGroupWorkWithOpportunity : doneGroupWorkWithoutOpportunity;
        }

        if (isEKGOpp) {
            return hasOpportunityId ? doneEKGAloneWithOpportunity : doneEKGAloneWithoutOpportunity;
        }

        return hasOpportunityId ? doneWithOpportunity : doneWithoutOpportunity;
    }
    private isPreventEnrollUntillOccasionDate(resource: Resource): boolean {
        if (!resource.occasion_dates?.length) return false;
        if (resource.opportunity_id) return false;

        var preventEnrollUntillOccasion = false;

        resource.occasion_dates.forEach((occasion) => {
            if (
                !moment().isBetween(occasion.start_date, occasion.end_date, 'day', '[]') ||
                moment().isBefore(occasion.start_date) ||
                moment().isAfter(occasion.end_date)
            ) {
                preventEnrollUntillOccasion = !preventEnrollUntillOccasion ? false : true;
            } else {
                preventEnrollUntillOccasion = false;
            }
        });
        return preventEnrollUntillOccasion;
    }
    private onApprovalRequired(resource: Resource, user: User): Observable<Resource> {
        const actions = [
            { label: $localize`Cancel`, value: 0, class: 'null-btn' },
            { label: $localize`Request join`, value: 1, class: 'pos-btn' },
        ];
        this.mobile.updateSideNavSub({
            dataType: SideNaveDataTypes.EVENT_JOIN,
            data: {
                icon: 'task_alt',
                title: $localize`Ask for joining`,
                inputDesc: $localize`Send message to admin requesting to join`,
                resource: {
                    ...resource,
                    ...{ occasion_dates: resource.occasion_dates?.filter((date) => !date.ispassed) },
                },
                actions: actions,
                type: 'enrollRequest',
            },
        });
        return this.mobile.getSideNavResponseData().pipe(
            filter((data) => data.type === 'enrollRequest' && data.response),
            switchMap((data) =>
                this.resourceService.userRequestEventPermission({
                    resId: resource.id,
                    eventOccasionId: data.eventOccasionId,
                    message: { body: data.value },
                    eager: ['user_enrollment'],
                })
            )
        );
    }

    private onNotAllowed(resource: Resource, user: User, eventJoin: PlayableResourceSpec): Observable<Resource> {
        switch (eventJoin.checkType) {
            case ResourceCheckKeys.APPLICATION_DATE:
                this.takeClosedEvent();
                break;
            case ResourceCheckKeys.ENROLL:
                this.informRejectionStatus(resource);
                break;
            case ResourceCheckKeys.SEATS:
                this.takeFullyBookedEvent();
                break;
        }
        return of(resource);
    }

    private informRejectionStatus(resource: Resource) {
        const msgBody = resource.user_enrollment.admin?.message?.body;
        this.mobile
            .onNotifyUser({
                icon: resource.user_enrollment.approval_status == 'rejected' ? 'cancel' : 'pending_actions',
                title:
                    resource.user_enrollment.approval_status == 'rejected'
                        ? $localize`Enrollment Request rejected`
                        : resource.user_enrollment.approval_status == 'waiting'
                        ? $localize`Enrollment Request in waiting list`
                        : $localize`Enrollment Request pending`,
                contentBody:
                    resource.user_enrollment.approval_status == 'rejected'
                        ? $localize`Unfortunately the administrator has rejected you request to enroll in the ${ResourceTypes.label(
                              resource.type_id,
                              { capitalize: false }
                          )} ${resource.title}`
                        : resource.user_enrollment.approval_status == 'waiting'
                        ? $localize`The administrator has put you in the waiting list to enroll in the ${ResourceTypes.label(
                              resource.type_id,
                              { capitalize: false }
                          )} ${resource.title}`
                        : $localize`The administrator is still reviewing you request to enroll in the ${ResourceTypes.label(
                              resource.type_id,
                              { capitalize: false }
                          )} ${resource.title}`,
                positiveBtn: $localize`Ok`,
                iconClass: resource.user_enrollment.approval_status == 'rejected' ? 'red' : 'orange',
                titleClass: resource.user_enrollment.approval_status == 'rejected' ? 'red' : 'orange',
                secContentBody: msgBody
                    ? '<span>' + $localize`The administrator message: ` + `</span><span>${msgBody}</span>`
                    : null,
                secContentClass: msgBody ? 'reviewer-msg' : null,
                negativeBtn: $localize`Cancel`,
            })
            .pipe(takeUntil(this.destroy))
            .subscribe();
    }
    takeClosedEvent() {
        this.mobile
            .onNotifyUser({
                icon: 'event_busy',
                title: $localize`Event is closed`,
                contentBody: $localize`This event has passed the date or closed dates. Check for more Event dates.`,
                positiveBtn: $localize`Ok`,
            })
            .pipe(takeUntil(this.destroy))
            .subscribe();
    }
    private takeFullyBookedEvent() {
        this.mobile
            .onNotifyUser({
                icon: 'event_busy',
                title: $localize`Event is closed`,
                contentBody: $localize`This event is fully booked. Check for more Events.`,
                positiveBtn: $localize`Ok`,
            })
            .pipe(takeUntil(this.destroy))
            .subscribe();
    }
}
